import { useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from '@/components/icons';

// Sentinel page size meaning "show all rows on one page" (kept within the
// server-side pageSize cap so 'All' works on server-paginated tables too).
const ALL = 1_000_000;

/**
 * Compact single-row pagination footer for tables.
 *
 * Fits on one line at every viewport width — the row-size selector and
 * "N–M of total" hint sit left, page controls (First / Prev / N of M /
 * Next / Last) right. On phones the labels drop to icons; on tablets and
 * up the full "← Prev" / "Next →" text returns.
 *
 * Pass as the `footer` prop of `<Table>`. `page` is 1-based.
 */
export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  setPage: (p: number) => void;
  setPageSize: (n: number) => void;
  /** Default: [10, 25, 50, 100, 200]. */
  pageSizeOptions?: number[];
}

const DEFAULT_OPTIONS = [10, 25, 50, 100, 200];

export function Pagination({
  page,
  pageSize,
  total,
  setPage,
  setPageSize,
  pageSizeOptions = DEFAULT_OPTIONS,
}: PaginationProps) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  const canPrev = page > 1;
  const canNext = page < lastPage;
  const [goto, setGoto] = useState('');
  function jump() {
    const n = parseInt(goto, 10);
    if (Number.isFinite(n)) setPage(Math.min(lastPage, Math.max(1, n)));
    setGoto('');
  }
  return (
    <div className="flex items-center justify-between gap-2 text-[11px] text-[var(--text-muted)]">
      <div className="flex min-w-0 items-center gap-1.5">
        <select
          value={pageSize >= ALL ? ALL : pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          title="Rows per page"
          className="rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] px-1.5 py-0.5 text-xs font-semibold text-[var(--text-primary)] outline-none focus:border-accent"
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
          <option value={ALL}>All</option>
        </select>
        <span className="truncate">
          <span className="hidden sm:inline">Showing </span>
          {first}–{last}
          <span className="hidden xs:inline"> of {total}</span>
        </span>
      </div>
      <div className="flex items-center gap-0.5">
        {lastPage > 1 && (
          <div className="mr-1 hidden items-center gap-1 sm:flex">
            <span className="text-[10px] font-semibold uppercase tracking-wide">Go to</span>
            <input
              value={goto}
              onChange={(e) => setGoto(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); jump(); } }}
              onBlur={() => { if (goto) jump(); }}
              placeholder="#"
              aria-label="Go to page"
              className="h-6 w-10 rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] px-1 text-center text-[11px] font-bold text-[var(--text-primary)] outline-none focus:border-accent"
            />
          </div>
        )}
        <NavBtn onClick={() => setPage(1)} disabled={!canPrev} title="First page">
          <ChevronsLeft size={13} strokeWidth={2.5} />
        </NavBtn>
        <NavBtn onClick={() => setPage(page - 1)} disabled={!canPrev} title="Previous page">
          <ChevronLeft size={13} strokeWidth={2.5} />
          <span className="hidden sm:inline">Prev</span>
        </NavBtn>
        <span className="px-1.5 text-[11px] font-bold text-[var(--text-secondary)]">
          {page}/{lastPage}
        </span>
        <NavBtn onClick={() => setPage(page + 1)} disabled={!canNext} title="Next page">
          <span className="hidden sm:inline">Next</span>
          <ChevronRight size={13} strokeWidth={2.5} />
        </NavBtn>
        <NavBtn onClick={() => setPage(lastPage)} disabled={!canNext} title="Last page">
          <ChevronsRight size={13} strokeWidth={2.5} />
        </NavBtn>
      </div>
    </div>
  );
}

function NavBtn({
  children,
  onClick,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="inline-flex h-6 items-center gap-0.5 rounded-md border border-[var(--border-color)] bg-white/[0.03] px-1.5 text-[11px] font-bold text-[var(--text-secondary)] transition hover:bg-accent/10 hover:text-accent disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white/[0.03] disabled:hover:text-[var(--text-secondary)]"
    >
      {children}
    </button>
  );
}
